import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Box, Button, Stack, Typography } from '@mui/material';
import { useStyles } from './styles';
import { Circle } from '@mui/icons-material';
import CircularProgress from '@mui/material/CircularProgress';
import { CustomModal, ModalActions } from 'components/CustomModal';
import { useNewCustomTranslations } from 'hooks';
import { CustomTransComponent } from 'components/CustomTransComponent';
import PowerSettingsNew from '@mui/icons-material/PowerSettingsNew';
import { useTranslation } from 'react-i18next';

export type GroupState = 'DISABLED' | 'PARTIAL' | 'ENABLED' | 'PENDING';
export type ChangeTrigger = 'toggle' | 'turnAllOn' | 'turnAllOff';
type TargetState = Exclude<GroupState, 'PENDING'>;
type ToggleState = GroupState;

const STATE_COLORS: Record<ToggleState, { background: string; icon: string }> = {
    DISABLED: { background: '#C6CACC', icon: '#fff' },
    PENDING: { background: '#007bc1', icon: '#fff' },
    PARTIAL: { background: '#F8D58F', icon: '#F0AA1F' },
    ENABLED: { background: '#b3d7ec', icon: '#007BC1' },
};

const TRANSLATE_MAP: Record<ToggleState, number> = {
    DISABLED: 0,
    PARTIAL: 1,
    PENDING: 1,
    ENABLED: 2,
};

const isGroupState = (state: GroupState | null | undefined): state is GroupState =>
    state === 'ENABLED' || state === 'DISABLED' || state === 'PARTIAL' || state === 'PENDING';

interface CustomSwitchBtnProps {
    id: number | string;
    totalLoadCount: number;
    enabledLoadCount: number;
    onChange?: (id: number | string, newState: TargetState, trigger: ChangeTrigger) => Promise<void> | void;
    componentToggleStyle?: boolean;
    disabled?: boolean;
    loadGroupName?: string;
    isEnabled?: boolean | null;
    groupState?: GroupState | null;
}

export const CustomSwitchBtn: React.FC<CustomSwitchBtnProps> = ({
    id,
    totalLoadCount,
    enabledLoadCount,
    onChange,
    componentToggleStyle = false,
    disabled = false,
    loadGroupName = 'load group',
    isEnabled = null,
    groupState = null,
}) => {
    const classes = useStyles();
    const { t } = useTranslation();
    const [selected, setSelected] = useState<ToggleState>('DISABLED');
    const [isIndeterminateModalVisible, setIsIndeterminateModalVisible] = useState(false);
    const [isConfirmModalVisible, setIsConfirmModalVisible] = useState(false);
    const [pendingTargetState, setPendingTargetState] = useState<TargetState | null>(null);
    const [isSubmitting, setIsSubmitting] = useState(false);
    useNewCustomTranslations('WIDGETS');
    const getStateFromProps = useCallback((): ToggleState => {
        if (isGroupState(groupState)) {
            return groupState;
        }
        if (isEnabled === true) return 'ENABLED';
        if (isEnabled === false) return 'DISABLED';

        if (enabledLoadCount > 0 && enabledLoadCount < totalLoadCount) {
            return 'PARTIAL';
        }

        if (enabledLoadCount === totalLoadCount && totalLoadCount > 0) {
            return 'ENABLED';
        }

        return 'DISABLED';
    }, [enabledLoadCount, groupState, isEnabled, totalLoadCount]);

    useEffect(() => {
        if (!isSubmitting) {
            setSelected(getStateFromProps());
        }
    }, [getStateFromProps, isSubmitting]);

    const runChange = async (targetState: TargetState, trigger: ChangeTrigger): Promise<void> => {
        setSelected('PENDING');
        setIsSubmitting(true);

        try {
            await onChange?.(id, targetState, trigger);
            setSelected(targetState);
        } catch (error) {
            setSelected(getStateFromProps());
            throw error;
        } finally {
            setIsSubmitting(false);
            setPendingTargetState(null);
        }
    };

    const handleClick = (targetState: TargetState): void => {
        if (disabled || isSubmitting || selected === 'PENDING') {
            return;
        }

        if (selected === 'PARTIAL') {
            setIsIndeterminateModalVisible(true);
            return;
        }

        if (targetState === selected) {
            return;
        }

        setPendingTargetState(targetState);
        setIsConfirmModalVisible(true);
    };

    const handleTurnAllOn = (): void => {
        setIsIndeterminateModalVisible(false);
        void runChange('ENABLED', 'turnAllOn');
    };

    const handleTurnAllOff = (): void => {
        setIsIndeterminateModalVisible(false);
        void runChange('DISABLED', 'turnAllOff');
    };
    const handleWrapperClick = (): void => {
        if (disabled || isSubmitting || isIndeterminateModalVisible || isConfirmModalVisible) {
            return;
        }

        if (selected === 'PARTIAL') {
            setIsIndeterminateModalVisible(true);
            return;
        }

        if (selected === 'ENABLED') {
            handleClick('DISABLED');
            return;
        }

        handleClick('ENABLED');
    };

    const isTurnOff = pendingTargetState === 'DISABLED';
    const confirmLabel = isTurnOff ? 'Turn Off' : 'Turn On';
    const confirmColor = isTurnOff ? '#CA3C3D' : '#2CA618';
    const confirmHeader = isTurnOff
        ? t('WIDGETS:LOADS_GROUPS.TURN_LOAD_GROUP_OFF')
        : t('WIDGETS:LOADS_GROUPS.TURN_LOAD_GROUP_ON');

    const confirmSubHeader = useMemo(
        () =>
            isTurnOff
                ? `${t('WIDGETS:LOADS_GROUPS.TURN_LOAD_GROUP_OFF_DESCRIPTION')} ${loadGroupName}`
                : `${t('WIDGETS:LOADS_GROUPS.TURN_LOAD_GROUP_ON_DESCRIPTION')} ${loadGroupName}`,
        [isTurnOff, loadGroupName]
    );

    const actions: ModalActions[] = [
        {
            key: 'cancel',
            label: t('COMMON:CANCEL'),
            variant: 'outlined',
            handleClick: (): void => {
                setIsConfirmModalVisible(false);
                setPendingTargetState(null);
            },
        },
        {
            key: 'confirm',
            label: <Typography>{confirmLabel}</Typography>,
            variant: 'contained',
            styles: { backgroundColor: confirmColor, color: '#fff', borderRadius: 50 },
            icon: <PowerSettingsNew />,
            handleClick: (): void => {
                setIsConfirmModalVisible(false);
                if (pendingTargetState) {
                    void runChange(pendingTargetState, 'toggle');
                }
            },
        },
    ];
    if (selected === 'PENDING') {
        return <CircularProgress size="20px" />;
    }

    return (
        <Box
            className={componentToggleStyle ? classes.componentSwitchToggleWrapper : classes.switchToggleWrapper}
            sx={{ backgroundColor: STATE_COLORS[selected].background, cursor: disabled ? 'none' : 'pointer' }}
            onClick={handleWrapperClick}
        >
            <Box
                className={classes.movableCircle}
                sx={{
                    transform: `translateX(${TRANSLATE_MAP[selected] * 100}%)`,
                    backgroundColor: STATE_COLORS[selected].background,
                }}
            >
                <Circle
                    className={componentToggleStyle ? classes.componentSwitchCircleIcon : classes.switchCircleIcon}
                    sx={{
                        backgroundColor: STATE_COLORS[selected].icon,
                        color: STATE_COLORS[selected].icon,
                    }}
                />
            </Box>

            <span className={classes.switchToggleLabel} />
            <span className={classes.switchToggleLabel} />
            <span className={classes.switchToggleLabel} />

            <CustomModal
                isOpen={isIndeterminateModalVisible}
                type="primary"
                header="Load Group in Indeterminate State"
                actions={[
                    {
                        key: 'cancel',
                        label: t('COMMON:CANCEL'),
                        variant: 'outlined',
                        handleClick: () => setIsIndeterminateModalVisible(false),
                    },
                ]}
                subHeader={[
                    <Stack key="modal-content">
                        <Typography variant="body1" fontWeight={400} mb={2}>
                            <CustomTransComponent translationKey={'WIDGETS:LOADS_GROUPS.TURN_ALL_LOAD_GROUPS'} />
                        </Typography>
                        <Button variant="outlined" sx={{ mb: 2 }} onClick={handleTurnAllOn}>
                            <CustomTransComponent translationKey={'WIDGETS:LOADS_GROUPS.TURN_ALL_LOADS_ON'} />
                        </Button>
                        <Button variant="outlined" onClick={handleTurnAllOff}>
                            <CustomTransComponent translationKey={'WIDGETS:LOADS_GROUPS.TURN_ALL_LOADS_OFF'} />
                        </Button>
                    </Stack>,
                ]}
                hidePadding
                actionsDivider
            />

            <CustomModal
                isOpen={isConfirmModalVisible}
                type="primary"
                header={[
                    <Stack key="modal-header">
                        <Typography fontSize={20} fontWeight={600} color="#424e54">
                            {confirmHeader}
                        </Typography>
                    </Stack>,
                ]}
                actions={actions}
                subHeader={[
                    <Stack key="modal-content">
                        <Typography fontSize={16} fontWeight={400} mb={2}>
                            {confirmSubHeader}
                        </Typography>
                    </Stack>,
                ]}
                hidePadding
                actionsDivider
            />
        </Box>
    );
};
